import { PLAYER } from '../enum';
import { PLAYER_STATE } from '@videotools/js/components/player/enums';

class PlayerBus {
	bus = null;
	player = null;
	componentHandlers = {
		getDuration: null,
		getPosition: null,
	};

	constructor(bus) {
		this.bus = bus;
	}

	setPlayer(player, handlers) {
		this.player = player;
		this.componentHandlers = handlers;
	}

	get ref() {
		return this.player;
	}

	get element() {
		return this.player?.$refs?.video;
	}

	get volume() {
		return this.player?.getVolume();
	}

	get duration() {
		if (!this.componentHandlers.getDuration) return 0;
		return this.componentHandlers.getDuration();
	}

	get mediaDuration() {
		return this.player?.mediaDuration ?? 0;
	}

	get liveSyncPosition() {
		return this.player?.liveSyncPosition ?? undefined;
	}

	get position() {
		if (!this.componentHandlers.getPosition) return 0;
		return this.componentHandlers.getPosition();
	}

	get isPaused() {
		return this.player.state === PLAYER_STATE.PAUSED;
	}

	get isPlaying() {
		return !this.isPaused;
	}

	emitReady() {
		this.bus.publish(PLAYER.ON.READY);
	}

	emitPlay(eventId) {
		this.bus.publish(PLAYER.ON.PLAY, eventId);
	}

	emitPause() {
		this.bus.publish(PLAYER.ON.PAUSE);
	}

	emitMediaChange(url) {
		this.bus.publish(PLAYER.ON.MEDIA_CHANGE, url);
	}

	emitProgress(seconds) {
		this.bus.publish(PLAYER.ON.PROGRESS, seconds);
	}

	subscribePosition(handler) {
		this.bus.subscribe(PLAYER.ON.PROGRESS, handler);
	}

	subscribeReady(handler) {
		this.bus.subscribe(PLAYER.ON.READY, handler);
	}

	unsubscribePosition(handler) {
		this.bus.unsubscribe(PLAYER.ON.PROGRESS, handler);
	}

	play() {
		this.bus.publish(PLAYER.REQUEST.PLAY);
	}

	pause() {
		this.bus.publish(PLAYER.REQUEST.PAUSE);
	}

	setMedia(url) {
		this.bus.publish(PLAYER.REQUEST.SET_MEDIA, url);
	}

	seek(seconds) {
		this.bus.publish(PLAYER.REQUEST.SEEK, seconds);
	}

	seekInterval({ start, duration }) {
		this.bus.publish(PLAYER.REQUEST.SEEK_INTERVAL, {
			start,
			duration,
		});
	}

	seekToLive() {
		this.bus.publish(PLAYER.REQUEST.SEEK_TO_LIVE);
	}

	seekTag(tag) {
		this.bus.publish(PLAYER.REQUEST.SEEK_TAG, tag);
	}

	setVolume(value) {
		this.bus.publish(PLAYER.REQUEST.SET_VOLUME, value);
	}

	mute() {
		this.bus.publish(PLAYER.REQUEST.MUTE);
	}

	unmute() {
		this.bus.publish(PLAYER.REQUEST.UNMUTE);
	}

	async snap() {
		return new Promise((resolve) => {
			this.bus.once(PLAYER.ON.SNAP, (image) => resolve(image));
			this.bus.publish(PLAYER.REQUEST.SNAP);
		});
	}

	toggleFullscreen() {
		this.player.toggleFullscreen();
	}

	seekBackward(seconds) {
		if (this.player.isAdMode) return;
		this.player.seekBackward(seconds);
	}

	seekForward(seconds) {
		if (this.player.isAdMode) return;
		this.player.seekForward(seconds);
	}
}

export default PlayerBus;
